'use client';

import * as React from 'react';
import { TriangleAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * The global error boundary.
 *
 * Users never see a stack trace. The digest is shown because it is the one
 * thing that lets support match a report to a logged incident.
 */
export default function GlobalError({ error, reset }: { error: Error & { digest?: string }; reset: () => void }) {
  React.useEffect(() => {
    // The server has already logged and reported this; log the digest on the
    // client so a browser console session can be tied to it too.
    console.error('CPCel error', error.digest ?? error.message);
  }, [error]);

  return (
    <main id="main" className="flex min-h-dvh flex-col items-center justify-center px-6 text-center">
      <div className="flex size-11 items-center justify-center rounded-full bg-danger-soft">
        <TriangleAlert className="size-5 text-danger-ink" aria-hidden="true" />
      </div>
      <h1 className="mt-3 text-xl font-semibold text-ink">Something went wrong</h1>
      <p className="mt-2 max-w-sm text-sm text-ink-muted">
        We could not load this screen. Nothing you have already saved has been affected. Please try again.
      </p>
      {error.digest ? (
        <p className="mt-2 font-mono text-xs text-ink-subtle">Reference: {error.digest}</p>
      ) : null}
      <div className="mt-5 flex gap-2">
        <Button onClick={reset}>Try again</Button>
        <Button variant="secondary" asChild>
          <a href="/dashboard">Go to dashboard</a>
        </Button>
      </div>
    </main>
  );
}
